import { useCallback, useMemo, useState } from 'react'
import { Trans } from 'react-i18next'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  ColumnAdapter,
  ColumnConditionValue,
  ConditionNumericOperator,
  ConditionTextOperator,
  DATE_OPERATOR_WITH_RANGE_RHS,
  DATE_OPERATOR_WITH_RHS,
  DefaultConditionOperator,
  DefaultVariableResult,
  NUMERIC_OPERATOR_WITH_INPUT_RHS,
  NUMERIC_OPERATOR_WITH_RANGE_RHS,
  OPERATOR_WITH_BETWEEN_RHS,
  OPERATOR_WITH_CASE_SENSITIVE,
  OPERATOR_WITH_INPUT_RHS,
  TableAdapter,
  VariableSelectFeatureFlag,
} from '@makyoapp/makyo-common'
import { ChevronDown, Trash16 } from '@makyoapp/makyo-icon/icon'
import { Button, ButtonVariant, Switch, SwitchSize } from '@makyoapp/makyo-ui'

import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { getDataSourceKeyForComponent } from '@/shared/builder/utils/components'
import { getOperatorDefaultOption } from '@/shared/condition'
import { ColumnConditionOperatorSelect } from '@/components/Builder/ColumnConditionOperatorSelect'
import { ColumnConditionSelect } from '@/components/Builder/ColumnConditionSelect'
import { RepeaterButton } from '@/components/RepeaterButton'
import { VariableSelectV2 } from '@/components/VariableSelectV2/VariableSelectV2'

const DEFAULT_FLAGS =
  VariableSelectFeatureFlag.SingleCell |
  VariableSelectFeatureFlag.CurrentRow |
  VariableSelectFeatureFlag.CustomText |
  VariableSelectFeatureFlag.ProfileData |
  VariableSelectFeatureFlag.SessionID |
  VariableSelectFeatureFlag.PageTitle

export interface ConditionItemProps {
  id?: string
  index: number
  colOptions: ColumnAdapter[]
  tables: TableAdapter[]
  table: TableAdapter
  value: ColumnConditionValue
  onChange: (nextValue: ColumnConditionValue) => void
  flags?: VariableSelectFeatureFlag
  actionOptions?: any[]
}

export function ConditionItem({
  id,
  index,
  colOptions,
  tables,
  table,
  value,
  onChange,
  flags = DEFAULT_FLAGS,
  actionOptions,
}: ConditionItemProps) {
  const cond = value.conditions[index]
  const { activeComponent, activeLayerContainer } = useActiveLayerContext()

  const isList = activeComponent?.groupId === 'list'

  const dataSourceKey = getDataSourceKeyForComponent(activeComponent?.compId || '')

  const theTable: any = dataSourceKey
    ? (activeLayerContainer?.elementSettings?.[activeComponent?.id as string]?.[dataSourceKey] ?? null)
    : null

  const activeTableId = theTable ?? table.id

  const colObj = useMemo(() => {
    return colOptions.find((c) => c.id === cond.lhs)
  }, [colOptions, cond.lhs])

  const varColTypes = useMemo(() => {
    if (colObj) {
      if (colObj.type === ColumnTypes.RowID || colObj.type === ColumnTypes.SessionID) {
        return []
      }

      if (
        colObj.type === ColumnTypes.Text ||
        [ConditionTextOperator.IsNotContainedIn, ConditionTextOperator.IsContainedIn].includes(
          cond.operator as ConditionTextOperator
        )
      ) {
        return [ColumnTypes.Text, ColumnTypes.Array]
      } else {
        return [colObj.type]
      }
    } else {
      return []
    }
  }, [colObj, cond])

  const needRHS =
    OPERATOR_WITH_INPUT_RHS.includes(cond.operator) ||
    DATE_OPERATOR_WITH_RHS.includes(cond.operator) ||
    NUMERIC_OPERATOR_WITH_RANGE_RHS.includes(cond.operator as ConditionNumericOperator) ||
    DATE_OPERATOR_WITH_RANGE_RHS.includes(cond.operator)

  const needRHSEndValue =
    NUMERIC_OPERATOR_WITH_RANGE_RHS.includes(cond.operator as ConditionNumericOperator) ||
    DATE_OPERATOR_WITH_RANGE_RHS.includes(cond.operator)

  const shouldRenderRange = needRHS && needRHSEndValue && OPERATOR_WITH_BETWEEN_RHS.includes(cond.operator)

  // Feature flags calculation function
  const getRhsFeatureFlags = useCallback(
    (operator: DefaultConditionOperator) => {
      const baseFlags = flags | VariableSelectFeatureFlag.ActionOutput
      const dataSourceFlag = isList ? VariableSelectFeatureFlag.ColumnData : VariableSelectFeatureFlag.CurrentRow
      const isDateOperator = [...DATE_OPERATOR_WITH_RANGE_RHS, ...DATE_OPERATOR_WITH_RHS].includes(operator)

      return isDateOperator
        ? baseFlags | VariableSelectFeatureFlag.DateTime | dataSourceFlag
        : baseFlags | dataSourceFlag
    },
    [flags, isList]
  )

  const rhsFeatureFlags = getRhsFeatureFlags(cond.operator)

  const rhsEndFeatureFlags = useMemo(() => {
    const baseFlags = flags | VariableSelectFeatureFlag.ActionOutput
    const isDateRangeOperator = DATE_OPERATOR_WITH_RANGE_RHS.includes(cond.operator)

    return isDateRangeOperator ? baseFlags | VariableSelectFeatureFlag.DateTime : baseFlags
  }, [flags, cond.operator])

  return (
    <div className='flex flex-col gap-2 p-2 border bdr rounded-md bg-zinc-50 dark:bg-slate-700'>
      <div className='flex gap-3 items-center'>
        <div className='text-sm font-medium text-gray-700 dark:text-gray-400'>Condition {index + 1}</div>
        <span
          className={`px-4 py-1.5 bg-gray-200 dark:bg-slate-900 text-gray-700 dark:text-gray-300 font-bold text-sm rounded-md shadow-sm ${index === value.conditions.length - 1 ? 'invisible' : ''}`}
        >
          {value.isAny ? 'OR' : 'AND'}
        </span>
      </div>
      <div className='flex w-full gap-2 items-center'>
        <div
          className='grid gap-2 w-full'
          style={{
            gridTemplateColumns: needRHS && !shouldRenderRange ? '1fr 1fr 1fr' : '1fr 1fr',
          }}
        >
          <div className='w-full min-w-0'>
            <ColumnConditionSelect
              id={id ? `${id}-lhs` : undefined}
              value={cond?.lhs}
              onChange={(lhs) => {
                const nextCond = [...value.conditions]
                const isSame = nextCond[index].lhs === lhs
                const newColObj = colOptions.find((c) => c.id === lhs)
                const nextOp = isSame
                  ? nextCond[index].operator
                  : newColObj
                    ? getOperatorDefaultOption(newColObj, table, tables)
                    : ConditionTextOperator.IsNotEmpty

                let extra: Record<string, unknown> = {}
                if (!isSame) {
                  extra = {
                    rhs: {
                      flag: VariableSelectFeatureFlag.CustomText,
                      value:
                        NUMERIC_OPERATOR_WITH_INPUT_RHS.includes(nextOp as ConditionNumericOperator) ||
                        NUMERIC_OPERATOR_WITH_RANGE_RHS.includes(nextOp as ConditionNumericOperator)
                          ? '0'
                          : '',
                    },
                  }

                  if (NUMERIC_OPERATOR_WITH_RANGE_RHS.includes(nextOp as ConditionNumericOperator)) {
                    extra.rhsEnd = {
                      flag: VariableSelectFeatureFlag.CustomText,
                      value: '0',
                    }
                  }
                }

                if (!isSame) {
                  if (DATE_OPERATOR_WITH_RHS.includes(nextOp)) {
                    extra = {
                      rhs: {
                        flag: VariableSelectFeatureFlag.CustomText,
                        value: new Date().toISOString(),
                      },
                    }
                  }
                  if (DATE_OPERATOR_WITH_RANGE_RHS.includes(nextOp)) {
                    extra = {
                      rhs: {
                        flag: VariableSelectFeatureFlag.CustomText,
                        value: new Date().toISOString(),
                      },
                      rhsEnd: {
                        flag: VariableSelectFeatureFlag.CustomText,
                        value: new Date().toISOString(),
                      },
                    }
                  }
                }
                nextCond[index] = {
                  ...nextCond[index],
                  lhs,
                  operator: nextOp,
                  ...extra,
                }
                onChange({
                  isAny: value?.isAny ?? true,
                  conditions: nextCond,
                })
              }}
              colOptions={colOptions}
            />
          </div>
          <ColumnConditionOperatorSelect
            id={id ? `${id}-op` : undefined}
            selected={cond.operator}
            value={cond}
            colOptions={colOptions}
            table={table}
            onChange={(opt) => {
              const nextCond = [...value.conditions]
              const currentCondition = nextCond[index]
              const currentOp = currentCondition.operator

              // Check if switching between range and single value operators
              const isCurrentBetween =
                DATE_OPERATOR_WITH_RANGE_RHS.includes(currentOp) ||
                NUMERIC_OPERATOR_WITH_RANGE_RHS.includes(currentOp as ConditionNumericOperator)
              const isNewBetween =
                DATE_OPERATOR_WITH_RANGE_RHS.includes(opt) ||
                NUMERIC_OPERATOR_WITH_RANGE_RHS.includes(opt as ConditionNumericOperator)

              // Reset values when switching between single/range operators
              if (isCurrentBetween !== isNewBetween) {
                if (isNewBetween) {
                  // Switching to range: use current value as start
                  nextCond[index] = {
                    ...currentCondition,
                    operator: opt,
                    rhs: currentCondition.rhs,
                    rhsEnd: currentCondition.rhs ? { ...(currentCondition.rhs as DefaultVariableResult) } : undefined,
                  }
                } else {
                  // Switching to single: use start value
                  nextCond[index] = {
                    ...currentCondition,
                    operator: opt,
                    rhs: currentCondition.rhs,
                    rhsEnd: undefined, // Clear end value
                  }
                }
              } else {
                // Same type of operator, just update the operator
                nextCond[index] = {
                  ...currentCondition,
                  operator: opt,
                }
              }

              onChange({
                isAny: value?.isAny ?? true,
                conditions: nextCond,
              })
            }}
          />

          {needRHS && (
            <div
              className='flex flex-col gap-2'
              style={{
                gridColumn: shouldRenderRange ? 'span 2 / span 2' : 'span 1 / span 1',
              }}
            >
              {shouldRenderRange && (
                <div className='uppercase text-m-ss'>
                  <Trans>Lower Bound</Trans>
                </div>
              )}
              <VariableSelectV2
                id={`${id}-rhs-var`}
                value={cond.rhs}
                onChange={(val) => {
                  const nextCond = [...value.conditions]
                  nextCond[index] = {
                    ...nextCond[index],
                    rhs: val as DefaultVariableResult,
                  }
                  onChange({
                    isAny: value?.isAny ?? true,
                    conditions: nextCond,
                  })
                }}
                tableId={isList ? activeTableId : table.id}
                featureFlags={rhsFeatureFlags}
                variableCustomTextOptions={{
                  inputType: colObj
                    ? colObj.type === ColumnTypes.Number
                      ? 'number'
                      : colObj.type === ColumnTypes.DateTime
                        ? 'datetime'
                        : 'text'
                    : 'text',
                }}
                variableColumnOptions={{
                  columnTypes: varColTypes,
                  excludeColIds: colObj?.id ? [colObj.id] : [], // * dont allow the same column to be selected twice, since it will create a circular reference
                }}
                options={actionOptions}
              />
            </div>
          )}

          {needRHSEndValue && (
            <div
              className='flex flex-col gap-2'
              style={{
                gridColumn: shouldRenderRange ? 'span 2 / span 2' : 'span 1 / span 1',
              }}
            >
              {shouldRenderRange && (
                <div className='uppercase text-m-ss'>
                  <Trans>Upper Bound</Trans>
                </div>
              )}
              <VariableSelectV2
                id={`${id}-rhs-var-end`}
                value={cond.rhsEnd}
                onChange={(val) => {
                  const nextCond = [...value.conditions]
                  nextCond[index] = {
                    ...nextCond[index],
                    rhsEnd: val as DefaultVariableResult,
                  }
                  onChange({
                    isAny: value?.isAny ?? true,
                    conditions: nextCond,
                  })
                }}
                featureFlags={rhsEndFeatureFlags}
                variableCustomTextOptions={{
                  inputType: NUMERIC_OPERATOR_WITH_RANGE_RHS.includes(cond.operator as ConditionNumericOperator)
                    ? 'number'
                    : 'datetime',
                }}
                variableColumnOptions={{
                  columnTypes: colObj ? [colObj.type] : [],
                  excludeColIds: colObj?.id ? [colObj.id] : [],
                }}
                options={actionOptions}
              />
            </div>
          )}
        </div>
        <Button
          variant={ButtonVariant.Ghost}
          icon={<Trash16 />}
          onClick={() => {
            const nextCond = [...value.conditions]
            nextCond.splice(index, 1)
            onChange?.({
              isAny: value?.isAny,
              conditions: nextCond,
            })
          }}
          squared
          noPadding
        />
      </div>
      <div className='flex-c w-full'>
        {OPERATOR_WITH_CASE_SENSITIVE.includes(cond.operator) && (
          <label className='flex-c'>
            <div>
              <Switch
                size={SwitchSize.XS}
                checked={cond.caseSensitive}
                onClick={(isChecked) => {
                  const nextCond = [...value.conditions]
                  nextCond[index] = {
                    ...nextCond[index],
                    caseSensitive: isChecked,
                  }
                  onChange({
                    isAny: value?.isAny ?? true,
                    conditions: nextCond,
                  })
                }}
              ></Switch>
            </div>
            <div className='text-m-sm whitespace-nowrap'>
              <Trans>Case Sensitive</Trans>
            </div>
          </label>
        )}
      </div>
    </div>
  )
}

export interface ActionWorkflowColumnRepeaterProps {
  id?: string
  onChange: (nextValue: ColumnConditionValue) => void
  value: ColumnConditionValue
  colOptions: ColumnAdapter[]
  tables: TableAdapter[]
  table: TableAdapter
  noUserSpecific?: boolean
  actionOptions?: any[]
}

export function ActionWorkflowColumnRepeater({
  id,
  value,
  onChange,
  colOptions,
  tables,
  table,
  actionOptions,
}: ActionWorkflowColumnRepeaterProps) {
  const [showLogicDropdown, setShowLogicDropdown] = useState(false)

  return (
    <div id={id} className='flex flex-col gap-4 w-full'>
      {/* Logic Mode Selector */}
      <div>
        <div className='relative'>
          <button
            onClick={() => setShowLogicDropdown(!showLogicDropdown)}
            className='flex items-center justify-between w-full bg-white dark:bg-slate-900 text-gray-700 dark:text-gray-300 px-4 py-3 rounded border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-500 transition-colors'
          >
            <span>{value?.isAny ? 'At least one condition must be met (OR)' : 'All conditions must be met (AND)'}</span>
            <ChevronDown className='h-4 w-4' />
          </button>

          {showLogicDropdown && (
            <div className='absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-900 border border-gray-300 dark:border-gray-600 rounded shadow-lg z-10'>
              <button
                onClick={() => {
                  onChange?.({
                    ...value,
                    isAny: false,
                  })
                  setShowLogicDropdown(false)
                }}
                className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                  !value?.isAny ? 'text-teal-500' : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                <Trans>All conditions must be met</Trans>
              </button>
              <button
                onClick={() => {
                  onChange?.({
                    ...value,
                    isAny: true,
                  })
                  setShowLogicDropdown(false)
                }}
                className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                  value?.isAny ? 'text-teal-500' : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                <Trans>At least one condition must be met</Trans>
              </button>
            </div>
          )}
        </div>
      </div>

      {value && value.conditions.length > 0 && (
        <div className='flex gap-4 flex-col w-full'>
          {value.conditions.map((_, i) => {
            return (
              <ConditionItem
                id={id}
                index={i}
                key={i}
                colOptions={colOptions}
                tables={tables}
                table={table}
                value={value}
                onChange={onChange}
                actionOptions={actionOptions}
              />
            )
          })}
        </div>
      )}

      <RepeaterButton
        id={id ? `${id}-repeat` : undefined}
        onClick={() => {
          onChange({
            isAny: value?.isAny ?? true,
            conditions: [
              ...(value?.conditions ?? []),
              {
                lhs: colOptions[0]?.id,
                operator: getOperatorDefaultOption(colOptions[0], table, tables),
                caseSensitive: false,
                userSpecific: false,
                rhs: undefined,
              },
            ],
          })
        }}
      >
        <Trans>Add Condition</Trans>
      </RepeaterButton>
    </div>
  )
}
